import { useState, useMemo, useEffect, useCallback, useRef } from "react"
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  updateEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  MarkerType,
  SelectionMode,
} from "reactflow"
import type { Node, Edge, NodeTypes, NodeChange, XYPosition, Connection } from "reactflow"
import "reactflow/dist/style.css"
import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  AlertTriangle,
  Check,
  Code2,
  Crosshair,
  GitBranch,
  Grid3X3,
  History,
  MousePointer2,
  RotateCcw,
  RotateCw,
  Save,
  Search,
  Share2,
  Square,
  SquareDashedMousePointer,
  X,
} from "lucide-react"
import { useHotkeys } from "react-hotkeys-hook"
import toast from "react-hot-toast"

import { applyLayout } from "./layout"
import { CustomNode } from "./components/CustomNode"
import { HtmlNode } from "./components/HtmlNode"
import { GroupNode } from "./components/GroupNode"
import { LayerBands } from "./components/LayerBands"
import { FilePicker } from "./components/FilePicker"
import { ExportButton } from "./components/ExportButton"
import { DiagramPreviewCard } from "./components/DiagramPreviewCard"
import {
  applyDiagramMetadataCommit,
  applyNodeBodyCommit,
  applyNodeDrop,
  applyNodeResizeEnd,
  commitNodeBody,
  copySelection,
  deleteSelection,
  getDroppedNodePlacement,
  getNodeBody,
  isFinalDimensionChange,
  makeUniqueId,
  moveNodeZOrder,
  orderNodesForReactFlow,
  pasteSelection as pasteCopiedSelection,
  reconnectEdge,
  resizeSectionToContents,
  snapPosition,
  SNAP_GRID_SIZE,
} from "./diagramEditorModel"
import type { CopiedSelection } from "./diagramEditorModel"
import {
  pushUndoEntry,
  redoDiagramEdit as redoHistoryEdit,
  undoDiagramEdit as undoHistoryEdit,
} from "./diagramHistoryModel"
import { analyzeDiagramQuality } from "./diagramQualityModel"
import type { DiagramQualityIssue } from "./diagramQualityModel"
import { brainSystemDiagram } from "./data/brain-system"
import { aiStackDiagram } from "./data/ai-stack"
import { lrPipeline } from "./data/example-lr-pipeline"
import { swimlaneDiagram } from "./data/example-swimlane"
import { radialDiagram } from "./data/example-radial"
import { horizontalBands } from "./data/example-horizontal-bands"
import { matrixDiagram } from "./data/example-matrix"
import { compoundDiagram } from "./data/example-compound"
import { showcaseDiagram } from "./data/example-showcase"
import { compoundCompactDiagram } from "./data/example-compound-compact"
import { verticalPipelineDiagram } from "./data/example-vertical-pipeline"
import { vidmaxSystemAtlas } from "./data/vidmax-system-atlas"
import { vidmaxPillarUgc } from "./data/vidmax-pillar-ugc"
import { vidmaxDiagram } from "./data/vidmax"
import { vidmaxPipelines } from "./data/vidmax-pipelines"
import { v1Default, v2Compact, v3Fields, v4Minimal, v5Dense } from "./data/vidmax-variants"
import { resolvePrefs } from "./types"
import type { DiagramSpec, DiagramNode as DNode } from "./types"
import { validateSpec } from "./validator"
import { trpc } from "~/lib/trpc"

interface DiagrammerExportBounds {
  x: number
  y: number
  width: number
  height: number
}

declare global {
  interface Window {
    __diagrammerCenterForExport?: () => Promise<boolean>
    __diagrammerGetExportBounds?: () => DiagrammerExportBounds | null
  }
}

const nodeTypes: NodeTypes = { custom: CustomNode, html: HtmlNode, group: GroupNode }
const DEFAULT_DIAGRAM_ID = "vidmax-system-atlas"

const DIAGRAMS: Record<string, DiagramSpec> = {
  "vidmax-system-atlas": vidmaxSystemAtlas,
  "vidmax-pillar-ugc": vidmaxPillarUgc,
  "vidmax-pipeline": vidmaxDiagram,
  "vidmax-pipelines": vidmaxPipelines,
  "v1-default": v1Default,
  "v2-compact": v2Compact,
  "v3-fields": v3Fields,
  "v4-minimal": v4Minimal,
  "v5-dense": v5Dense,
  "brain-system": brainSystemDiagram,
  "ai-stack": aiStackDiagram,
  "lr-pipeline": lrPipeline,
  swimlane: swimlaneDiagram,
  radial: radialDiagram,
  "horizontal-bands": horizontalBands,
  matrix: matrixDiagram,
  compound: compoundDiagram,
  showcase: showcaseDiagram,
  "compound-compact": compoundCompactDiagram,
  "vertical-pipeline": verticalPipelineDiagram,
}

const DIAGRAM_LIST = Object.values(DIAGRAMS).sort((a, b) => a.name.localeCompare(b.name))

function getInitialDiagram(): DiagramSpec {
  const hash = window.location.hash.slice(1)
  return DIAGRAMS[hash] ?? DIAGRAMS[DEFAULT_DIAGRAM_ID]
}

const SECTION_LABEL = "text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-2"
const TEXT_BUTTON =
  "inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
const TOOL_BUTTON =
  "inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
const AUTOSAVE_DEBOUNCE_MS = 1200
const VERSION_SNAPSHOT_INTERVAL_MS = 5 * 60 * 1000
const HOSTED_POLL_INTERVAL_MS = 5000
const HISTORY_LIMIT = 80
const DEBUG_STORAGE_KEY = "diagrammer:debug"
const ALL_NODES_VIEW_ID = "all-nodes"
const ALL_NODES_VIEW_NAME = "All Nodes"

type ShapeTool = "select" | "node" | "section"

interface HostedDiagramState {
  id: string
  slug: string
}

function isDiagramSpec(value: unknown): value is DiagramSpec {
  return validateSpec(value).filter((issue) => issue.severity === "error").length === 0
}

function getDefaultLayout(diagram: DiagramSpec) {
  return diagram.views.find((view) => view.id === diagram.defaultView)?.layout ?? diagram.views[0]?.layout ?? "manual"
}

function ensureAllNodesView(diagram: DiagramSpec): DiagramSpec {
  const allNodesView = {
    id: ALL_NODES_VIEW_ID,
    name: ALL_NODES_VIEW_NAME,
    layout: getDefaultLayout(diagram),
  }
  const otherViews = diagram.views.filter((view) => view.id !== ALL_NODES_VIEW_ID)

  return {
    ...diagram,
    views: [allNodesView, ...otherViews],
    defaultView: ALL_NODES_VIEW_ID,
  }
}

function ensureManualView(diagram: DiagramSpec): DiagramSpec {
  const withAllNodesView = ensureAllNodesView(diagram)
  const allNodesView = withAllNodesView.views.find((view) => view.id === ALL_NODES_VIEW_ID)
  if (allNodesView?.layout === "manual") return withAllNodesView

  return {
    ...withAllNodesView,
    views: withAllNodesView.views.map((view) =>
      view.id === ALL_NODES_VIEW_ID ? { ...view, layout: "manual" as const } : view,
    ),
  }
}

function normalizeInitialView(diagram: DiagramSpec): DiagramSpec {
  const defaultView = diagram.views.find((view) => view.id === diagram.defaultView)
  if (defaultView?.layout !== "manual") return ensureAllNodesView(diagram)

  const positionedCount = diagram.nodes.filter((node) => Boolean(node.position)).length
  const mostlyUnpositioned = positionedCount < diagram.nodes.length / 2
  if (!mostlyUnpositioned) return ensureAllNodesView(diagram)

  const generatedView = diagram.views.find((view) => view.layout !== "manual")
  if (!generatedView) return ensureAllNodesView(diagram)

  return ensureAllNodesView({
    ...diagram,
    defaultView: generatedView.id,
  })
}

function getDefaultNodeLayer(diagram: DiagramSpec) {
  return diagram.layers.find((layer) => layer.id !== "section" && layer.id !== "sections")?.id ?? diagram.layers[0]?.id
}

function getDefaultSectionLayer(diagram: DiagramSpec) {
  return diagram.layers.find((layer) => layer.id === "section" || layer.id === "sections")?.id ?? diagram.layers[0]?.id
}

function getPublicDiagramUrl(slug: string) {
  return `${window.location.origin}/diagrammer/${slug}`
}

function getShapeCopy(shape: Exclude<ShapeTool, "select" | "section">) {
  return {
    node: {
      label: "Node",
      description: "Editable AIXML node.",
      type: undefined,
      icon: undefined,
    },
  }[shape]
}

function areStringArraysEqual(left: string[], right: string[]) {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

function isDiagrammerDebugEnabled() {
  if (typeof window === "undefined") return false
  const flag = window.localStorage.getItem(DEBUG_STORAGE_KEY)
  return flag === "1" || (window.location.hostname === "localhost" && flag !== "0")
}

function debugDiagrammer(event: string, data?: Record<string, unknown>) {
  if (!isDiagrammerDebugEnabled()) return
  console.debug(`[diagrammer] ${event}`, data ?? {})
}

function summarizeNodeChanges(changes: NodeChange[]) {
  return changes.map((change) => ({
    type: change.type,
    id: "id" in change ? change.id : undefined,
    dragging: "dragging" in change ? change.dragging : undefined,
    resizing: "resizing" in change ? change.resizing : undefined,
    hasPosition: "position" in change && Boolean(change.position),
    hasDimensions: "dimensions" in change && Boolean(change.dimensions),
  }))
}

function isTextInputTarget(event: KeyboardEvent) {
  const target = event.target
  if (!(target instanceof HTMLElement)) return false
  const tagName = target.tagName.toLowerCase()
  return target.isContentEditable || tagName === "input" || tagName === "textarea" || tagName === "select"
}

function getDiagrammerExportBounds(activeNodeIds: ReadonlySet<string>): DiagrammerExportBounds | null {
  const nodes = [...document.querySelectorAll<HTMLElement>(".diagrammer-export-ready .react-flow__node")]
  const rects = nodes
    .map((node) => {
      const nodeId = node.dataset.id
      if (nodeId && !activeNodeIds.has(nodeId)) return null

      const style = window.getComputedStyle(node)
      if (style.display === "none" || style.visibility === "hidden") return null
      const opacity = Number(style.opacity)
      if (Number.isFinite(opacity) && opacity < 0.2) return null

      const rect = node.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return null
      return rect
    })
    .filter((rect): rect is DOMRect => rect !== null)

  if (rects.length === 0) return null

  const left = Math.max(0, Math.floor(Math.min(...rects.map((rect) => rect.left))))
  const top = Math.max(0, Math.floor(Math.min(...rects.map((rect) => rect.top))))
  const right = Math.min(window.innerWidth, Math.ceil(Math.max(...rects.map((rect) => rect.right))))
  const bottom = Math.min(window.innerHeight, Math.ceil(Math.max(...rects.map((rect) => rect.bottom))))
  const width = right - left
  const height = bottom - top

  if (width <= 0 || height <= 0) return null
  return { x: left, y: top, width, height }
}

function KeyBadge({ value }: { value: string }) {
  return (
    <kbd className="ml-1 rounded border border-border bg-secondary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-muted-foreground">
      {value}
    </kbd>
  )
}

function getNodeSearchText(node: DNode) {
  return [
    node.id,
    getNodeBody(node),
    node.type,
    node.layer,
    node.icon,
    node.group,
    node.fields
      ? Object.entries(node.fields)
          .map(([key, value]) => `${key} ${value}`)
          .join(" ")
      : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
}

function getNumericSize(value: number | string | undefined, fallback: number) {
  if (typeof value === "number") return value
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}

function formatVersionTime(value: string | Date) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Unknown time"
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function getQualityTone(severity: DiagramQualityIssue["severity"]) {
  return {
    error: "border-red-500/40 bg-red-500/10 text-red-200",
    warning: "border-amber-500/40 bg-amber-500/10 text-amber-100",
    suggestion: "border-sky-500/40 bg-sky-500/10 text-sky-100",
  }[severity]
}

interface DiagramAppProps {
  renderMode?: boolean
}

function DiagramApp({ renderMode = false }: DiagramAppProps) {
  const reactFlow = useReactFlow()
  const canvasRef = useRef<HTMLDivElement | null>(null)
  const diagramHeaderEditorRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const renderCountRef = useRef(0)
  const [diagram, setDiagram] = useState<DiagramSpec>(() => normalizeInitialView(getInitialDiagram()))
  const [fileName, setFileName] = useState("vidmax.json")
  const [activeView, setActiveView] = useState(diagram.defaultView)
  const [activeLayers, setActiveLayers] = useState<Set<string>>(new Set(diagram.layers.map((l) => l.id)))
  const [selectedNode, setSelectedNode] = useState<DNode | null>(null)
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([])
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null)
  const [hostedDiagram, setHostedDiagram] = useState<HostedDiagramState | null>(null)
  const [selectedHostedId, setSelectedHostedId] = useState<string | null>(null)
  const [pendingLineSourceId, setPendingLineSourceId] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [copiedSelection, setCopiedSelection] = useState<CopiedSelection | null>(null)
  const [activeTool, setActiveTool] = useState<ShapeTool>("select")
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null)
  const [isEditingDiagramHeader, setIsEditingDiagramHeader] = useState(false)
  const [diagramHeaderDraft, setDiagramHeaderDraft] = useState({ name: "", description: "" })
  const [showRawEditor, setShowRawEditor] = useState(false)
  const [rawDiagramDraft, setRawDiagramDraft] = useState("")
  const [rawDiagramError, setRawDiagramError] = useState<string | null>(null)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [activeSearchMatchIndex, setActiveSearchMatchIndex] = useState(-1)
  const [renderExportReady, setRenderExportReady] = useState(!renderMode)
  const [alignmentGuide, setAlignmentGuide] = useState<{ x?: number; y?: number } | null>(null)
  const [showRegistryHome, setShowRegistryHome] = useState(() => {
    if (renderMode) return false
    const pathId =
      typeof window === "undefined" ? "" : (window.location.pathname.split("/diagrammer/")[1]?.split("/")[0] ?? "")
    const hashId = typeof window === "undefined" ? "" : window.location.hash.slice(1)
    return !pathId && !hashId
  })
  const [historyVersion, setHistoryVersion] = useState(0)
  const saveModeRef = useRef<"manual" | "auto">("manual")
  const lastVersionedAtRef = useRef<Date | null>(null)
  const pendingVersionCreatedRef = useRef(false)
  const undoStackRef = useRef<DiagramSpec[]>([])
  const redoStackRef = useRef<DiagramSpec[]>([])
  const loadedHostedKeyRef = useRef<string | null>(null)
  const autoCenteredViewKeyRef = useRef<string | null>(null)

  const commitDiagram = useCallback(
    (updater: (previous: DiagramSpec) => DiagramSpec, options?: { manualView?: boolean }) => {
      debugDiagrammer("commit:start", { manualView: options?.manualView ?? true })
      setDiagram((previous) => {
        const editablePrevious = options?.manualView === false ? previous : ensureManualView(previous)
        const next = updater(editablePrevious)
        if (next === previous) {
          debugDiagrammer("commit:no-op", {
            nodes: previous.nodes.length,
            edges: previous.edges.length,
          })
          return previous
        }

        setIsDirty(true)
        const nextHistory = pushUndoEntry(
          { undoStack: undoStackRef.current, redoStack: redoStackRef.current },
          previous,
          HISTORY_LIMIT,
        )
        undoStackRef.current = nextHistory.undoStack
        redoStackRef.current = nextHistory.redoStack
        setHistoryVersion((value) => value + 1)

        debugDiagrammer("commit:finish", {
          previousNodes: previous.nodes.length,
          nextNodes: next.nodes.length,
          previousEdges: previous.edges.length,
          nextEdges: next.edges.length,
        })
        if (options?.manualView === false) return next

        setActiveView(ALL_NODES_VIEW_ID)
        setActiveLayers(new Set(next.layers.map((l) => l.id)))
        return { ...next, defaultView: ALL_NODES_VIEW_ID }
      })
    },
    [],
  )

  const updateNodeById = useCallback(
    (nodeId: string, body: string) => {
      commitDiagram((previous) => applyNodeBodyCommit(previous, nodeId, body), { manualView: false })
      setSelectedNode((previous) => (previous?.id === nodeId ? commitNodeBody(previous, body) : previous))
    },
    [commitDiagram],
  )

  const startEditingDiagramHeader = () => {
    setDiagramHeaderDraft({
      name: diagram.name,
      description: diagram.description ?? "",
    })
    setIsEditingDiagramHeader(true)
  }

  const commitDiagramHeader = useCallback(() => {
    setIsEditingDiagramHeader(false)
    commitDiagram(
      (previous) => applyDiagramMetadataCommit(previous, diagramHeaderDraft.name, diagramHeaderDraft.description),
      { manualView: false },
    )
  }, [commitDiagram, diagramHeaderDraft.description, diagramHeaderDraft.name])

  const cancelDiagramHeaderEdit = () => {
    setIsEditingDiagramHeader(false)
    setDiagramHeaderDraft({ name: "", description: "" })
  }

  const navigateToDiagramRegistry = () => {
    window.history.pushState(null, "", "/diagrammer")
    loadedHostedKeyRef.current = null
    setSelectedHostedId(null)
    setHostedDiagram(null)
    setShowRegistryHome(true)
  }

  const openRawEditor = () => {
    setRawDiagramDraft(JSON.stringify(diagram, null, 2))
    setRawDiagramError(null)
    setShowRawEditor(true)
  }

  const applyRawDiagram = () => {
    let parsed: unknown
    try {
      parsed = JSON.parse(rawDiagramDraft)
    } catch (error) {
      setRawDiagramError(`Invalid JSON: ${(error as Error).message}`)
      return
    }

    const validationErrors = validateSpec(parsed).filter((issue) => issue.severity === "error")
    if (validationErrors.length > 0) {
      setRawDiagramError(validationErrors.map((issue) => `${issue.path}: ${issue.message}`).join("\n"))
      return
    }

    const nextDiagram = normalizeInitialView(parsed as DiagramSpec)
    commitDiagram(() => nextDiagram, { manualView: false })
    const nextView = nextDiagram.views.find((view) => view.id === nextDiagram.defaultView)
    setActiveView(nextDiagram.defaultView)
    setActiveLayers(new Set(nextView?.layers ?? nextDiagram.layers.map((layer) => layer.id)))
    setSelectedNode(null)
    setSelectedNodeIds([])
    setSelectedEdgeId(null)
    setEditingNodeId(null)
    setRawDiagramError(null)
    setShowRawEditor(false)
    toast.success("AIXML applied")
  }

  const prefs = useMemo(() => resolvePrefs(diagram.preferences), [diagram.preferences])
  const currentView = useMemo(
    () =>
      diagram.views.find((v) => v.id === activeView) ?? {
        id: ALL_NODES_VIEW_ID,
        name: ALL_NODES_VIEW_NAME,
        layout: "manual" as const,
      },
    [activeView, diagram.views],
  )
  const qualityResult = useMemo(() => analyzeDiagramQuality(diagram, currentView.id), [currentView.id, diagram])
  const selectedEdge = useMemo(
    () => diagram.edges.find((edge) => edge.id === selectedEdgeId) ?? null,
    [diagram.edges, selectedEdgeId],
  )
  const trpcUtils = trpc.useUtils()
  const hostedList = trpc.diagrammer.list.useQuery({ limit: 100 }, { enabled: !renderMode })
  const activeHostedId = selectedHostedId ?? hostedDiagram?.id ?? null
  const hostedById = trpc.diagrammer.getById.useQuery(
    { id: activeHostedId ?? "00000000-0000-0000-0000-000000000000" },
    {
      enabled: Boolean(activeHostedId) && !showRegistryHome && !renderMode,
      refetchInterval: HOSTED_POLL_INTERVAL_MS,
      refetchIntervalInBackground: true,
    },
  )
  const pathDiagramId =
    typeof window === "undefined"
      ? ""
      : (window.location.pathname.split("/diagrammer/")[1]?.split("/")[0] ??
        window.location.pathname.split("/diagrammer-render/")[1]?.split("/")[0] ??
        "")
  const initialHash = typeof window === "undefined" ? "" : window.location.hash.slice(1)
  const requestedDiagramId = pathDiagramId || initialHash
  const renderReady = !renderMode || (Boolean(hostedDiagram) && hostedDiagram.slug === requestedDiagramId)
  const exportReady = renderReady && (!renderMode || renderExportReady)
  const shouldLoadRequestedDiagram =
    Boolean(requestedDiagramId) &&
    requestedDiagramId !== "new" &&
    !DIAGRAMS[requestedDiagramId] &&
    !hostedDiagram &&
    !showRegistryHome
  const hostedBySlug = trpc.diagrammer.getBySlug.useQuery(
    { slug: requestedDiagramId || "missing" },
    {
      enabled: shouldLoadRequestedDiagram,
      refetchInterval: renderMode ? false : HOSTED_POLL_INTERVAL_MS,
      refetchIntervalInBackground: !renderMode,
    },
  )
  const hostedVersions = trpc.diagrammer.versions.useQuery(
    { diagramId: activeHostedId ?? "00000000-0000-0000-0000-000000000000" },
    {
      enabled: Boolean(activeHostedId) && !renderMode,
    },
  )

  const createDiagram = trpc.diagrammer.create.useMutation({
    onSuccess: ({ diagram: saved }) => {
      loadedHostedKeyRef.current = `${saved.id}:${String(saved.updatedAt)}`
      lastVersionedAtRef.current = new Date()
      setHostedDiagram({ id: saved.id, slug: saved.slug })
      setIsDirty(false)
      setLastSavedAt(new Date())
      window.history.replaceState(null, "", `/diagrammer/${saved.slug}`)
      void trpcUtils.diagrammer.list.invalidate()
      void trpcUtils.diagrammer.versions.invalidate({ diagramId: saved.id })
      toast.success("Diagram saved")
    },
    onError: (error) => toast.error(error.message || "Failed to save diagram"),
  })

  const updateDiagramMutation = trpc.diagrammer.update.useMutation({
    onSuccess: ({ diagram: saved }) => {
      loadedHostedKeyRef.current = `${saved.id}:${String(saved.updatedAt)}`
      if (pendingVersionCreatedRef.current) lastVersionedAtRef.current = new Date()
      pendingVersionCreatedRef.current = false
      setHostedDiagram({ id: saved.id, slug: saved.slug })
      setIsDirty(false)
      setLastSavedAt(new Date())
      void trpcUtils.diagrammer.list.invalidate()
      void trpcUtils.diagrammer.versions.invalidate({ diagramId: saved.id })
      if (saveModeRef.current === "manual") toast.success("Diagram saved")
    },
    onError: (error) => {
      pendingVersionCreatedRef.current = false
      toast.error(error.message || "Failed to save diagram")
    },
  })

  const revertDiagramMutation = trpc.diagrammer.revert.useMutation({
    onSuccess: ({ diagram: saved }) => {
      loadedHostedKeyRef.current = `${saved.id}:${String(saved.updatedAt)}`
      lastVersionedAtRef.current = new Date()
      if (!isDiagramSpec(saved.specJson)) {
        toast.error("Reverted diagram spec is invalid")
        return
      }
      loadDiagram(saved.specJson, `${saved.slug}.json`, { id: saved.id, slug: saved.slug })
      void trpcUtils.diagrammer.list.invalidate()
      void trpcUtils.diagrammer.versions.invalidate({ diagramId: saved.id })
      toast.success("Diagram reverted")
    },
    onError: (error) => toast.error(error.message || "Failed to revert diagram"),
  })

  const publishDiagram = trpc.diagrammer.publish.useMutation({
    onSuccess: ({ diagram: saved }) => {
      loadedHostedKeyRef.current = `${saved.id}:${String(saved.updatedAt)}`
      setHostedDiagram({ id: saved.id, slug: saved.slug })
      setIsDirty(false)
      setLastSavedAt(new Date())
      void trpcUtils.diagrammer.list.invalidate()
      toast.success("Publish link ready")
    },
    onError: (error) => toast.error(error.message || "Failed to publish diagram"),
  })

  const visibleNodeIds = useMemo(() => {
    const viewLayers = currentView.layers ?? diagram.layers.map((l) => l.id)
    const effective = viewLayers.filter((l) => activeLayers.has(l))
    const viewNodeIds = currentView.nodes ? new Set(currentView.nodes) : null
    return new Set(
      diagram.nodes
        .filter((n) => effective.includes(n.layer))
        .filter((n) => !viewNodeIds || viewNodeIds.has(n.id))
        .map((n) => n.id),
    )
  }, [diagram, currentView, activeLayers])

  const renderedNodeIds = useMemo(() => {
    if (!prefs.showContextNodes || !currentView.nodes) return visibleNodeIds
    return new Set(diagram.nodes.map((node) => node.id))
  }, [currentView.nodes, diagram.nodes, prefs.showContextNodes, visibleNodeIds])

  const visibleOrderedNodeIds = useMemo(
    () => orderNodesForReactFlow(diagram.nodes.filter((node) => visibleNodeIds.has(node.id))).map((node) => node.id),
    [diagram.nodes, visibleNodeIds],
  )
  const visibleOrderedNodeIdsKey = useMemo(() => visibleOrderedNodeIds.join("|"), [visibleOrderedNodeIds])

  const searchMatches = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return []

    return visibleOrderedNodeIds.filter((nodeId) => {
      const node = diagram.nodes.find((candidate) => candidate.id === nodeId)
      return node ? getNodeSearchText(node).includes(query) : false
    })
  }, [diagram.nodes, searchQuery, visibleOrderedNodeIds])

  const startEditingNode = useCallback(
    (nodeId: string) => {
      const spec = diagram.nodes.find((node) => node.id === nodeId)
      if (!spec) return

      setSelectedNode(spec)
      setSelectedNodeIds([spec.id])
      setSelectedEdgeId(null)
      setEditingNodeId(spec.id)
    },
    [diagram.nodes],
  )

  const { rfNodes, rfEdges } = useMemo(() => {
    const layerMap = new Map(diagram.layers.map((l) => [l.id, l]))

    const filteredNodes = diagram.nodes.filter((n) => renderedNodeIds.has(n.id))
    const orderedSpecNodes = orderNodesForReactFlow(filteredNodes)
    const rawNodes: Node[] = orderedSpecNodes.map((n) => {
      const body = getNodeBody(n)
      const isContextNode = !visibleNodeIds.has(n.id)
      const isSelected = selectedNodeIds.includes(n.id)
      const base: Node = {
        id: n.id,
        type: n.isGroup ? "group" : n.html ? "html" : "custom",
        position: { x: 0, y: 0 },
        data: {
          label: n.label,
          description: n.description,
          tooltip: n.tooltip,
          body,
          type: n.type,
          color: layerMap.get(n.layer)?.color ?? "#6366f1",
          layerName: layerMap.get(n.layer)?.name ?? n.layer,
          layer: n.layer,
          rank: n.rank ?? 0,
          html: n.html,
          width: n.width,
          height: n.height,
          backgroundColor: n.backgroundColor,
          fields: n.fields,
          icon: n.icon,
          searchQuery: isSearchOpen ? searchQuery.trim() : "",
          isGroup: n.isGroup,
          isSelected,
          isContextNode,
          specPosition: n.position,
          truncateDescriptions: prefs.truncateDescriptions,
          showFieldsOnNode: prefs.showFieldsOnNode,
          nodeWidth: n.width ?? prefs.nodeWidth,
          isEditing: editingNodeId === n.id,
          onEditStart: () => {
            startEditingNode(n.id)
          },
          onBodyCommit: (bodyValue: string) => {
            setEditingNodeId(null)
            updateNodeById(n.id, bodyValue)
          },
          onEditCancel: () => setEditingNodeId(null),
          onResizeEnd: (params: { x: number; y: number; width: number; height: number }) => {
            debugDiagrammer("node:resize-end", { id: n.id, params })
            commitDiagram((previous) => applyNodeResizeEnd(previous, { id: n.id, ...params }), { manualView: false })
          },
        },
      }
      base.style = {
        width: n.width ?? prefs.nodeWidth,
        height: n.isGroup ? (n.height ?? 300) : n.height,
        opacity: isContextNode ? 0.1 : undefined,
        pointerEvents: isContextNode ? "none" : undefined,
        zIndex: isContextNode ? 0 : n.isGroup ? 1 : isSelected ? 30 : (n.zIndex ?? 10),
      }
      if (n.parentNode && renderedNodeIds.has(n.parentNode)) {
        base.parentNode = n.parentNode
      }
      if (n.isGroup) {
        base.dragHandle = ".diagrammer-group-node-header"
      }
      return base
    })

    const rawEdges: Edge[] = diagram.edges
      .filter((e) => renderedNodeIds.has(e.source) && renderedNodeIds.has(e.target))
      .map((e) => {
        const isForegroundEdge = visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target)
        const isSelectedEdge = selectedEdgeId === e.id
        const edgeOpacity = isForegroundEdge ? 1 : 0.1
        const labelOpacity = isForegroundEdge ? 1 : 0.16

        return {
          id: e.id,
          source: e.source,
          target: e.target,
          label: prefs.showEdgeLabels ? e.label : undefined,
          type: e.style && e.style !== "default" && e.style !== "dashed" ? e.style : prefs.edgeStyle,
          animated: prefs.animateEdges && isForegroundEdge,
          updatable: true,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isSelectedEdge ? "#f59e0b" : isForegroundEdge ? prefs.edgeColor : `${prefs.edgeColor}33`,
          },
          interactionWidth: 28,
          labelStyle: {
            fill: `rgba(15, 23, 42, ${labelOpacity})`,
            opacity: labelOpacity,
          },
          labelBgStyle: {
            fill: `rgba(255, 255, 255, ${isForegroundEdge ? 0.86 : 0.08})`,
            fillOpacity: isForegroundEdge ? 0.86 : 0.08,
          },
          style: {
            stroke: isSelectedEdge ? "#f59e0b" : prefs.edgeColor,
            strokeOpacity: edgeOpacity,
            strokeWidth: isSelectedEdge ? 2.5 : isForegroundEdge ? 1.5 : 1,
            strokeDasharray: e.style === "dashed" ? "6,5" : undefined,
          },
        }
      })

    const laid = applyLayout(rawNodes, rawEdges, currentView.layout, {
      nodeWidth: prefs.nodeWidth,
      rankSpacing: prefs.rankSpacing,
      nodeGap: prefs.nodeGap,
    })
    debugDiagrammer("derive:rf", {
      layout: currentView.layout,
      rawNodes: rawNodes.length,
      rawEdges: rawEdges.length,
      selectedNodeIds,
      editingNodeId,
    })
    return { rfNodes: laid, rfEdges: rawEdges }
  }, [
    commitDiagram,
    diagram,
    renderedNodeIds,
    visibleNodeIds,
    currentView.layout,
    prefs,
    selectedNodeIds,
    selectedEdgeId,
    editingNodeId,
    updateNodeById,
    startEditingNode,
    isSearchOpen,
    searchQuery,
  ])

  const [nodes, setNodes, onNodesChange] = useNodesState(rfNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(rfEdges)

  const getCanvasNodePosition = useCallback(
    (nodeId: string, seen = new Set<string>()): XYPosition | null => {
      if (seen.has(nodeId)) return null
      seen.add(nodeId)

      const node = nodes.find((candidate) => candidate.id === nodeId)
      if (!node) return null

      if (!node.parentNode) return node.position

      const parentPosition = getCanvasNodePosition(node.parentNode, seen)
      if (!parentPosition) return node.position

      return {
        x: parentPosition.x + node.position.x,
        y: parentPosition.y + node.position.y,
      }
    },
    [nodes],
  )

  const focusCanvasNode = useCallback(
    (nodeId: string, zoom = 1.1) => {
      const canvasNode = nodes.find((candidate) => candidate.id === nodeId)
      const position = getCanvasNodePosition(nodeId)
      if (!canvasNode || !position) return

      const width = canvasNode.width ?? getNumericSize(canvasNode.style?.width, prefs.nodeWidth)
      const height =
        canvasNode.height ?? getNumericSize(canvasNode.style?.height, canvasNode.type === "group" ? 300 : 90)

      void reactFlow.setCenter(position.x + width / 2, position.y + height / 2, {
        duration: 280,
        zoom,
      })
    },
    [getCanvasNodePosition, nodes, prefs.nodeWidth, reactFlow],
  )

  const selectAndFocusNode = useCallback(
    (nodeId: string, zoom = 1.1) => {
      const spec = diagram.nodes.find((node) => node.id === nodeId)
      if (!spec) return

      setSelectedNode(spec)
      setSelectedNodeIds([spec.id])
      setSelectedEdgeId(null)
      setPendingLineSourceId(null)
      setEditingNodeId(null)
      setActiveTool("select")
      focusCanvasNode(spec.id, zoom)
    },
    [diagram.nodes, focusCanvasNode],
  )

  const focusSearchMatch = useCallback(
    (index: number) => {
      const matchId = searchMatches[index]
      if (!matchId) return

      setActiveSearchMatchIndex(index)
      selectAndFocusNode(matchId, 1.2)
    },
    [searchMatches, selectAndFocusNode],
  )

  const focusNextSearchMatch = useCallback(
    (direction: 1 | -1 = 1) => {
      if (searchMatches.length === 0) {
        if (searchQuery.trim()) toast.error("No matching nodes")
        return
      }

      let nextIndex = (activeSearchMatchIndex + direction + searchMatches.length) % searchMatches.length
      if (activeSearchMatchIndex === -1) {
        nextIndex = direction === 1 ? 0 : searchMatches.length - 1
      }
      focusSearchMatch(nextIndex)
    },
    [activeSearchMatchIndex, focusSearchMatch, searchMatches.length, searchQuery],
  )

  useEffect(() => {
    if (!isSearchOpen) return
    window.setTimeout(() => searchInputRef.current?.focus(), 0)
  }, [isSearchOpen])

  useEffect(() => {
    if (searchMatches.length === 0) {
      setActiveSearchMatchIndex(-1)
      return
    }
    if (activeSearchMatchIndex < searchMatches.length) return
    setActiveSearchMatchIndex(-1)
  }, [activeSearchMatchIndex, searchMatches.length])

  useEffect(() => {
    debugDiagrammer("sync:reactflow-state", {
      rfNodes: rfNodes.length,
      rfEdges: rfEdges.length,
      selectedNodeIds,
      editingNodeId,
    })
    setNodes(rfNodes)
    setEdges(rfEdges)
  }, [rfNodes, rfEdges, setNodes, setEdges, selectedNodeIds, editingNodeId])

  useEffect(() => {
    renderCountRef.current += 1
    debugDiagrammer("render", {
      renderCount: renderCountRef.current,
      diagramNodes: diagram.nodes.length,
      diagramEdges: diagram.edges.length,
      rfNodes: nodes.length,
      rfEdges: edges.length,
      selectedNodeIds,
      selectedEdgeId,
      isDirty,
    })
  })

  useEffect(() => {
    const hosted = hostedById.data?.diagram
    if (!hosted) return
    const hostedKey = `${hosted.id}:${String(hosted.updatedAt)}`
    if (loadedHostedKeyRef.current === hostedKey) return
    loadedHostedKeyRef.current = hostedKey
    if (!isDiagramSpec(hosted.specJson)) {
      toast.error("Hosted diagram spec is invalid")
      return
    }
    debugDiagrammer("hosted:apply-polled-update", { source: "id", id: hosted.id, updatedAt: String(hosted.updatedAt) })
    loadDiagram(hosted.specJson, `${hosted.slug}.json`, { id: hosted.id, slug: hosted.slug })
    setShowRegistryHome(false)
  }, [hostedById.data])

  useEffect(() => {
    const hosted = hostedBySlug.data?.diagram
    if (!hosted) return
    const hostedKey = `${hosted.id}:${String(hosted.updatedAt)}`
    if (loadedHostedKeyRef.current === hostedKey) return
    loadedHostedKeyRef.current = hostedKey
    if (!isDiagramSpec(hosted.specJson)) {
      toast.error("Hosted diagram spec is invalid")
      return
    }
    debugDiagrammer("hosted:apply-polled-update", {
      source: "slug",
      slug: hosted.slug,
      updatedAt: String(hosted.updatedAt),
    })
    loadDiagram(hosted.specJson, `${hosted.slug}.json`, { id: hosted.id, slug: hosted.slug })
    setShowRegistryHome(false)
  }, [hostedBySlug.data])

  useEffect(() => {
    const latestVersion = hostedVersions.data?.versions[0]
    if (!latestVersion) return

    const latestCreatedAt = new Date(latestVersion.createdAt)
    if (Number.isNaN(latestCreatedAt.getTime())) return

    const current = lastVersionedAtRef.current
    if (!current || latestCreatedAt > current) lastVersionedAtRef.current = latestCreatedAt
  }, [hostedVersions.data])

  useEffect(() => {
    lastVersionedAtRef.current = null
  }, [activeHostedId])

  const onNodeClick = (_: React.MouseEvent, node: Node) => {
    const spec = diagram.nodes.find((n) => n.id === node.id) ?? null
    if (pendingLineSourceId && spec && pendingLineSourceId !== spec.id) {
      addLineBetweenNodes(pendingLineSourceId, spec.id)
      setPendingLineSourceId(null)
      return
    }
    if (!spec) return

    setSelectedEdgeId(null)
    setSelectedNode((previous) => (previous?.id === spec.id ? previous : spec))
    setSelectedNodeIds((previous) => (areStringArraysEqual(previous, [spec.id]) ? previous : [spec.id]))
  }

  const onNodeDoubleClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.stopPropagation()
      startEditingNode(node.id)
    },
    [startEditingNode],
  )

  const onEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      setSelectedNode(null)
      setSelectedNodeIds([])
      setSelectedEdgeId((prev) => (prev === edge.id ? null : edge.id))
    },
    [setSelectedNode, setSelectedEdgeId],
  )

  const updateAlignmentGuide = useCallback(
    (draggedNode: Node) => {
      const threshold = SNAP_GRID_SIZE / 2
      const draggedWidth = draggedNode.width ?? getNumericSize(draggedNode.style?.width, prefs.nodeWidth)
      const draggedHeight = draggedNode.height ?? getNumericSize(draggedNode.style?.height, 90)
      const draggedCenter = {
        x: draggedNode.position.x + draggedWidth / 2,
        y: draggedNode.position.y + draggedHeight / 2,
      }
      let nextGuide: { x?: number; y?: number } = {}

      for (const candidate of nodes) {
        if (candidate.id === draggedNode.id || candidate.parentNode !== draggedNode.parentNode) continue

        const candidateWidth = candidate.width ?? getNumericSize(candidate.style?.width, prefs.nodeWidth)
        const candidateHeight = candidate.height ?? getNumericSize(candidate.style?.height, 90)
        const candidateCenter = {
          x: candidate.position.x + candidateWidth / 2,
          y: candidate.position.y + candidateHeight / 2,
        }

        if (Math.abs(candidate.position.x - draggedNode.position.x) <= threshold)
          nextGuide = { ...nextGuide, x: candidate.position.x }
        if (Math.abs(candidateCenter.x - draggedCenter.x) <= threshold)
          nextGuide = { ...nextGuide, x: candidateCenter.x }
        if (Math.abs(candidate.position.y - draggedNode.position.y) <= threshold)
          nextGuide = { ...nextGuide, y: candidate.position.y }
        if (Math.abs(candidateCenter.y - draggedCenter.y) <= threshold)
          nextGuide = { ...nextGuide, y: candidateCenter.y }
      }

      setAlignmentGuide(nextGuide.x === undefined && nextGuide.y === undefined ? null : nextGuide)
    },
    [nodes, prefs.nodeWidth],
  )

  const onNodeDrag = useCallback(
    (_: React.MouseEvent, node: Node) => {
      updateAlignmentGuide(node)
    },
    [updateAlignmentGuide],
  )

  const onNodeDragStop = useCallback(
    (_: React.MouseEvent, node: Node) => {
      setAlignmentGuide(null)
      debugDiagrammer("node:drag-stop", { id: node.id, position: node.position, parentNode: node.parentNode })
      commitDiagram((prev) => applyNodeDrop(prev, node, prefs), { manualView: false })
      setSelectedNode((previous) =>
        previous?.id === node.id
          ? {
              ...previous,
              ...getDroppedNodePlacement(node, previous, diagram, prefs),
            }
          : previous,
      )
    },
    [commitDiagram, diagram, prefs],
  )

  const onDiagramNodesChange = useCallback(
    (changes: NodeChange[]) => {
      debugDiagrammer("nodes:change", { changes: summarizeNodeChanges(changes) })
      onNodesChange(changes)
      const trackedChanges = changes.filter((change) => isFinalDimensionChange(change))
      if (trackedChanges.length === 0) return

      debugDiagrammer("nodes:final-dimensions:ignored", { changes: summarizeNodeChanges(trackedChanges) })
    },
    [onNodesChange],
  )

  const onSelectionChange = useCallback(
    ({ nodes: selectedNodes, edges: selectedEdges }: { nodes: Node[]; edges: Edge[] }) => {
      if (selectedNodes.length === 0 && selectedEdges.length === 0) return

      const nextNodeIds = selectedNodes.map((node) => node.id)
      debugDiagrammer("selection:change", {
        nodeIds: nextNodeIds,
        edgeIds: selectedEdges.map((edge) => edge.id),
      })
      setSelectedNodeIds((previous) => (areStringArraysEqual(previous, nextNodeIds) ? previous : nextNodeIds))

      if (selectedNodes.length === 1) {
        const spec = diagram.nodes.find((node) => node.id === selectedNodes[0]?.id) ?? null
        setSelectedNode((previous) => (previous?.id === spec?.id ? previous : spec))
      } else if (selectedNodes.length !== 1) {
        setSelectedNode((previous) => (previous === null ? previous : null))
      }

      const nextEdgeId = selectedEdges.length === 1 ? (selectedEdges[0]?.id ?? null) : null
      setSelectedEdgeId((previous) => (previous === nextEdgeId ? previous : nextEdgeId))
    },
    [diagram.nodes],
  )

  const toggleLayer = (id: string) => {
    setActiveLayers((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const setShowEdgeLabels = useCallback(
    (showEdgeLabels: boolean) => {
      commitDiagram(
        (previous) => ({
          ...previous,
          preferences: {
            ...previous.preferences,
            showEdgeLabels,
          },
        }),
        { manualView: false },
      )
    },
    [commitDiagram],
  )

  const switchView = (viewId: string) => {
    setActiveView(viewId)
    const view = diagram.views.find((v) => v.id === viewId)
    if (!view) return
    const viewLayers = view.layers ?? diagram.layers.map((l) => l.id)
    setActiveLayers(new Set(viewLayers))
    setSelectedNode(null)
    setSelectedNodeIds([])
    setSelectedEdgeId(null)
  }

  const loadDiagram = (spec: DiagramSpec, name: string, hosted?: HostedDiagramState) => {
    const normalizedSpec = normalizeInitialView(spec)
    if (!hosted) loadedHostedKeyRef.current = null
    autoCenteredViewKeyRef.current = null
    setRenderExportReady(!renderMode)
    setDiagram(normalizedSpec)
    setFileName(name)
    setActiveView(normalizedSpec.defaultView)
    const defaultView = normalizedSpec.views.find((view) => view.id === normalizedSpec.defaultView)
    setActiveLayers(new Set(defaultView?.layers ?? normalizedSpec.layers.map((l) => l.id)))
    setSelectedNode(null)
    setSelectedNodeIds([])
    setSelectedEdgeId(null)
    setHostedDiagram(hosted ?? null)
    setSelectedHostedId(null)
    setPendingLineSourceId(null)
    setIsDirty(false)
    setLastSavedAt(hosted ? new Date() : null)
    setActiveTool("select")
    setEditingNodeId(null)
    undoStackRef.current = []
    redoStackRef.current = []
    setHistoryVersion((value) => value + 1)
  }

  const updateEditableDiagram = useCallback(
    (updater: (previous: DiagramSpec) => DiagramSpec) => {
      commitDiagram(updater)
    },
    [commitDiagram],
  )

  const addShapeNode = (shape: Exclude<ShapeTool, "select" | "section">, position?: XYPosition) => {
    updateEditableDiagram((previous) => {
      const existingIds = new Set(previous.nodes.map((node) => node.id))
      const selectedSection = position ? null : selectedNode?.isGroup ? selectedNode : null
      const id = makeUniqueId(shape, existingIds)
      const nodeLayer = getDefaultNodeLayer(previous)
      const shapeCopy = getShapeCopy(shape)
      const node: DNode = {
        id,
        label: shapeCopy.label,
        description: shapeCopy.description,
        type: shapeCopy.type,
        icon: shapeCopy.icon,
        layer: nodeLayer,
        parentNode: selectedSection?.id,
        position: position
          ? snapPosition(position)
          : selectedSection
            ? { x: 32, y: 72 }
            : { x: 120 + previous.nodes.length * 24, y: 120 },
      }
      setSelectedNode(node)
      setSelectedNodeIds([node.id])
      setSelectedEdgeId(null)
      return { ...previous, nodes: [...previous.nodes, node] }
    })
  }

  const addNode = () => addShapeNode("node")

  const addSection = (position?: XYPosition) => {
    updateEditableDiagram((previous) => {
      const existingIds = new Set(previous.nodes.map((node) => node.id))
      const id = makeUniqueId("section", existingIds)
      const sectionLayer = getDefaultSectionLayer(previous)
      const node: DNode = {
        id,
        label: "Section",
        description: "Section container for grouping related nodes.",
        layer: sectionLayer,
        isGroup: true,
        width: 420,
        height: 300,
        position: position ? snapPosition(position) : { x: 80 + previous.nodes.length * 20, y: 80 },
      }
      setSelectedNode(node)
      setSelectedNodeIds([node.id])
      setSelectedEdgeId(null)
      return { ...previous, nodes: [node, ...previous.nodes] }
    })
  }

  const fixSectionBounds = useCallback(
    (sectionId: string) => {
      commitDiagram((previous) => resizeSectionToContents(previous, sectionId, prefs), { manualView: false })
      toast.success("Section bounds fixed")
    },
    [commitDiagram, prefs],
  )

  const moveSelectedNodeZOrder = useCallback(
    (direction: 1 | -1) => {
      const nodeId = selectedNodeIds[0]
      if (!nodeId) return
      commitDiagram((previous) => moveNodeZOrder(previous, nodeId, direction), { manualView: false })
    },
    [commitDiagram, selectedNodeIds],
  )

  const centerActiveView = useCallback(
    async (options?: { duration?: number; silent?: boolean }) => {
      const activeNodeIds = new Set(visibleOrderedNodeIds)
      const activeNodes = reactFlow.getNodes().filter((node) => activeNodeIds.has(node.id))
      if (activeNodes.length === 0) {
        if (!options?.silent) toast.error("No nodes in current view")
        return false
      }

      return Promise.resolve(
        reactFlow.fitView({
          nodes: activeNodes,
          padding: Math.max(prefs.fitPadding, 0.18),
          maxZoom: prefs.maxZoom,
          duration: options?.duration ?? 280,
        }),
      )
    },
    [prefs.fitPadding, prefs.maxZoom, reactFlow, visibleOrderedNodeIds],
  )

  useEffect(() => {
    if (!renderMode) return

    const activeNodeIds = new Set(visibleOrderedNodeIds)

    window.__diagrammerCenterForExport = async () => {
      setRenderExportReady(false)
      const centered = await centerActiveView({ duration: 0, silent: true })
      await new Promise((resolve) => window.requestAnimationFrame(() => resolve(null)))
      setRenderExportReady(true)
      return centered
    }
    window.__diagrammerGetExportBounds = () => getDiagrammerExportBounds(activeNodeIds)

    return () => {
      delete window.__diagrammerCenterForExport
      delete window.__diagrammerGetExportBounds
    }
  }, [centerActiveView, renderMode, visibleOrderedNodeIds])

  useEffect(() => {
    if (!renderMode) return

    const root = document.documentElement
    const body = document.body
    const hadDarkClass = root.classList.contains("dark")
    const previousColorScheme = root.style.colorScheme
    const previousBodyBackground = body.style.backgroundColor

    root.classList.remove("dark")
    root.style.colorScheme = "light"
    body.style.backgroundColor = "#ffffff"

    return () => {
      if (hadDarkClass) root.classList.add("dark")
      root.style.colorScheme = previousColorScheme
      body.style.backgroundColor = previousBodyBackground
    }
  }, [renderMode])

  useEffect(() => {
    if (showRegistryHome || !renderReady || visibleOrderedNodeIds.length === 0) return

    const autoCenterKey = `${activeView}:${visibleOrderedNodeIdsKey}`
    if (autoCenteredViewKeyRef.current === autoCenterKey) return
    autoCenteredViewKeyRef.current = autoCenterKey
    if (renderMode) setRenderExportReady(false)

    let secondFrame = 0
    let cancelled = false
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        void centerActiveView({ silent: true }).then(() => {
          if (!renderMode || cancelled) return
          window.requestAnimationFrame(() => {
            if (!cancelled) setRenderExportReady(true)
          })
        })
      })
    })

    return () => {
      cancelled = true
      window.cancelAnimationFrame(firstFrame)
      if (secondFrame) window.cancelAnimationFrame(secondFrame)
    }
  }, [
    activeView,
    centerActiveView,
    renderMode,
    renderReady,
    showRegistryHome,
    visibleOrderedNodeIds.length,
    visibleOrderedNodeIdsKey,
  ])

  const addLinesBetweenNodes = useCallback(
    (pairs: { source: string; target: string }[]) => {
      debugDiagrammer("edges:add-lines", { pairs })
      updateEditableDiagram((previous) => {
        const existingIds = new Set(previous.edges.map((edge) => edge.id))
        const newEdges = pairs.map((pair) => {
          const id = makeUniqueId("line", existingIds)
          existingIds.add(id)
          return {
            id,
            source: pair.source,
            target: pair.target,
          }
        })
        return {
          ...previous,
          edges: [...previous.edges, ...newEdges],
        }
      })
    },
    [updateEditableDiagram],
  )

  const addLineBetweenNodes = useCallback(
    (source: string, target: string) => {
      addLinesBetweenNodes([{ source, target }])
    },
    [addLinesBetweenNodes],
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target || connection.source === connection.target) return
      debugDiagrammer("edge:connect", {
        source: connection.source,
        target: connection.target,
        sourceHandle: connection.sourceHandle,
        targetHandle: connection.targetHandle,
      })
      addLineBetweenNodes(connection.source, connection.target)
    },
    [addLineBetweenNodes],
  )

  const onEdgeUpdate = useCallback(
    (oldEdge: Edge, connection: Connection) => {
      if (!connection.source || !connection.target || connection.source === connection.target) return

      setEdges((currentEdges) => updateEdge(oldEdge, connection, currentEdges))
      commitDiagram(
        (previous) =>
          reconnectEdge(previous, {
            edgeId: oldEdge.id,
            source: connection.source ?? undefined,
            target: connection.target ?? undefined,
          }),
        { manualView: false },
      )
      setSelectedEdgeId(oldEdge.id)
    },
    [commitDiagram, setEdges],
  )

  const startLineTool = useCallback(() => {
    const selectedNodes = selectedNodeIds
      .map((id) => diagram.nodes.find((node) => node.id === id))
      .filter((node): node is DNode => Boolean(node) && !node.isGroup)
    if (selectedNodes.length > 1) {
      const [source, ...targets] = selectedNodes
      if (!source) return
      addLinesBetweenNodes(targets.map((target) => ({ source: source.id, target: target.id })))
      toast.success(`Created ${targets.length} line${targets.length === 1 ? "" : "s"}`)
      return
    }

    if (!selectedNode) {
      toast.error("Select a source node first")
      return
    }
    setPendingLineSourceId(selectedNode.id)
    toast("Click another node to draw a line")
  }, [addLinesBetweenNodes, diagram.nodes, selectedNode, selectedNodeIds])

  const getPanePosition = (event: React.MouseEvent): XYPosition | null => {
    const bounds = canvasRef.current?.getBoundingClientRect()
    if (!bounds) return null
    return reactFlow.project({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    })
  }

  const handlePaneClick = (event: React.MouseEvent) => {
    const position = getPanePosition(event)
    if (position && activeTool !== "select") {
      if (activeTool === "section") {
        addSection(position)
      } else {
        addShapeNode(activeTool, position)
      }
      setActiveTool("select")
      return
    }

    setSelectedNode(null)
    setSelectedNodeIds([])
    setSelectedEdgeId(null)
    setEditingNodeId(null)
  }

  const copySelectedCanvasItems = useCallback(() => {
    const nextSelection = copySelection(diagram, selectedNodeIds)
    if (!nextSelection) return

    setCopiedSelection(nextSelection)
    toast.success(`Copied ${nextSelection.nodes.length} node${nextSelection.nodes.length === 1 ? "" : "s"}`)
  }, [diagram, selectedNodeIds])

  const pasteSelection = useCallback(
    (selection: CopiedSelection | null = copiedSelection) => {
      if (!selection || selection.nodes.length === 0) return

      commitDiagram((previous) => {
        const pasted = pasteCopiedSelection(previous, selection)

        setSelectedNode(pasted.selection.nodes.length === 1 ? (pasted.selection.nodes[0] ?? null) : null)
        setSelectedNodeIds(pasted.selection.nodes.map((node) => node.id))
        setSelectedEdgeId(null)
        setCopiedSelection(pasted.selection)

        return pasted.diagram
      })
    },
    [commitDiagram, copiedSelection],
  )

  const duplicateSelectedCanvasItems = useCallback(() => {
    const nextSelection = copySelection(diagram, selectedNodeIds)
    if (!nextSelection) return
    pasteSelection(nextSelection)
  }, [diagram, pasteSelection, selectedNodeIds])

  const deleteSelectedCanvasItems = useCallback(() => {
    if (selectedNodeIds.length === 0 && !selectedEdgeId) return

    commitDiagram((previous) => deleteSelection(previous, selectedNodeIds, selectedEdgeId), { manualView: false })
    setSelectedNode(null)
    setSelectedNodeIds([])
    setSelectedEdgeId(null)
    setEditingNodeId(null)
    toast.success("Deleted selection")
  }, [commitDiagram, selectedEdgeId, selectedNodeIds])

  const undoDiagramEdit = useCallback(() => {
    const result = undoHistoryEdit(
      { undoStack: undoStackRef.current, redoStack: redoStackRef.current },
      diagram,
      HISTORY_LIMIT,
    )
    if (!result.changed) return

    undoStackRef.current = result.undoStack
    redoStackRef.current = result.redoStack
    setDiagram(result.diagram)
    setIsDirty(true)
    setSelectedNode(null)
    setSelectedNodeIds([])
    setSelectedEdgeId(null)
    setEditingNodeId(null)
    setHistoryVersion((value) => value + 1)
  }, [diagram])

  const redoDiagramEdit = useCallback(() => {
    const result = redoHistoryEdit(
      { undoStack: undoStackRef.current, redoStack: redoStackRef.current },
      diagram,
      HISTORY_LIMIT,
    )
    if (!result.changed) return

    undoStackRef.current = result.undoStack
    redoStackRef.current = result.redoStack
    setDiagram(result.diagram)
    setIsDirty(true)
    setSelectedNode(null)
    setSelectedNodeIds([])
    setSelectedEdgeId(null)
    setEditingNodeId(null)
    setHistoryVersion((value) => value + 1)
  }, [diagram])

  const cycleSelectedNode = useCallback(
    (direction: 1 | -1) => {
      if (visibleOrderedNodeIds.length === 0) return

      const currentId = selectedNodeIds[0]
      const currentIndex = currentId ? visibleOrderedNodeIds.indexOf(currentId) : -1
      let nextIndex = (currentIndex + direction + visibleOrderedNodeIds.length) % visibleOrderedNodeIds.length
      if (currentIndex === -1) {
        nextIndex = direction === 1 ? 0 : visibleOrderedNodeIds.length - 1
      }

      const nextNodeId = visibleOrderedNodeIds[nextIndex]
      if (!nextNodeId) return
      selectAndFocusNode(nextNodeId)
    },
    [selectedNodeIds, selectAndFocusNode, visibleOrderedNodeIds],
  )

  const openDiagramSearch = useCallback(() => {
    setIsSearchOpen(true)
    setActiveTool("select")
    setEditingNodeId(null)
  }, [])

  useHotkeys(
    "n",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      setActiveTool("node")
    },
    { enableOnFormTags: true },
  )

  useHotkeys(
    "s",
    (event) => {
      if (isTextInputTarget(event) || event.metaKey || event.ctrlKey) return
      event.preventDefault()
      setActiveTool("section")
    },
    { enableOnFormTags: true },
  )

  useHotkeys(
    "l",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      startLineTool()
    },
    { enableOnFormTags: true },
    [startLineTool],
  )

  useHotkeys(
    "c",
    (event) => {
      if (isTextInputTarget(event) || event.metaKey || event.ctrlKey) return
      event.preventDefault()
      void centerActiveView()
    },
    { enableOnFormTags: true },
    [centerActiveView],
  )

  useHotkeys(
    "u",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      undoDiagramEdit()
    },
    { enableOnFormTags: true },
    [undoDiagramEdit],
  )

  useHotkeys(
    "r",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      redoDiagramEdit()
    },
    { enableOnFormTags: true },
    [redoDiagramEdit],
  )

  useHotkeys(
    "tab,shift+tab",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      cycleSelectedNode(event.shiftKey ? -1 : 1)
    },
    { enableOnFormTags: true },
    [cycleSelectedNode],
  )

  useHotkeys(
    "enter",
    (event) => {
      if (isTextInputTarget(event)) return
      if (selectedNodeIds.length !== 1) return

      event.preventDefault()
      startEditingNode(selectedNodeIds[0] ?? "")
    },
    { enableOnFormTags: true },
    [selectedNodeIds, startEditingNode],
  )

  useHotkeys(
    "meta+f,ctrl+f",
    (event) => {
      event.preventDefault()
      openDiagramSearch()
    },
    { enableOnFormTags: true },
    [openDiagramSearch],
  )

  useHotkeys(
    "meta+c,ctrl+c",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      copySelectedCanvasItems()
    },
    { enableOnFormTags: true },
    [copySelectedCanvasItems],
  )

  useHotkeys(
    "meta+v,ctrl+v",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      pasteSelection()
    },
    { enableOnFormTags: true },
    [pasteSelection],
  )

  useHotkeys(
    "meta+d,ctrl+d",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      duplicateSelectedCanvasItems()
    },
    { enableOnFormTags: true },
    [duplicateSelectedCanvasItems],
  )

  useHotkeys(
    "backspace,delete",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      deleteSelectedCanvasItems()
    },
    { enableOnFormTags: true },
    [deleteSelectedCanvasItems],
  )

  useHotkeys(
    "meta+z,ctrl+z",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      undoDiagramEdit()
    },
    { enableOnFormTags: true },
    [undoDiagramEdit],
  )

  useHotkeys(
    "shift+meta+z,shift+ctrl+z",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      redoDiagramEdit()
    },
    { enableOnFormTags: true },
    [redoDiagramEdit],
  )

  useHotkeys(
    "escape",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      setPendingLineSourceId(null)
      setActiveTool("select")
      setEditingNodeId(null)
      setSelectedNode(null)
      setSelectedNodeIds([])
      setSelectedEdgeId(null)
    },
    { enableOnFormTags: true },
  )

  const saveDiagram = useCallback(() => {
    const validationErrors = validateSpec(diagram).filter((issue) => issue.severity === "error")
    if (validationErrors.length > 0) {
      toast.error(`Fix ${validationErrors.length} AIXML errors before saving`)
      return
    }

    if (hostedDiagram) {
      saveModeRef.current = "manual"
      pendingVersionCreatedRef.current = true
      updateDiagramMutation.mutate({
        id: hostedDiagram.id,
        title: diagram.name,
        description: diagram.description ?? null,
        specJson: diagram as unknown as Record<string, unknown>,
        createVersion: true,
        versionLabel: "manual",
      })
      return
    }

    saveModeRef.current = "manual"
    createDiagram.mutate({
      title: diagram.name,
      description: diagram.description,
      project: diagram.id.startsWith("vidmax") ? "vidmax" : "aix",
      sourceType: "manual",
      visibility: "internal",
      specJson: diagram as unknown as Record<string, unknown>,
    })
  }, [createDiagram, diagram, hostedDiagram, updateDiagramMutation])

  useHotkeys(
    "meta+s,ctrl+s",
    (event) => {
      if (isTextInputTarget(event)) return
      event.preventDefault()
      saveDiagram()
    },
    { enableOnFormTags: true },
    [saveDiagram],
  )

  const publishCurrentDiagram = () => {
    if (!hostedDiagram) {
      toast.error("Save before publishing")
      return
    }
    publishDiagram.mutate({ id: hostedDiagram.id, visibility: "public-link" })
  }

  const copyPublishUrl = async () => {
    if (!hostedDiagram) {
      toast.error("Save before copying a link")
      return
    }
    await navigator.clipboard.writeText(getPublicDiagramUrl(hostedDiagram.slug))
    toast.success("Diagram URL copied")
  }

  const shouldCreateAutosaveVersion = useCallback(() => {
    const lastVersionedAt = lastVersionedAtRef.current
    if (!lastVersionedAt) return true
    return Date.now() - lastVersionedAt.getTime() >= VERSION_SNAPSHOT_INTERVAL_MS
  }, [])

  useEffect(() => {
    if (!hostedDiagram || !isDirty || updateDiagramMutation.isLoading) return

    const timeout = window.setTimeout(() => {
      const validationErrors = validateSpec(diagram).filter((issue) => issue.severity === "error")
      if (validationErrors.length > 0) return

      const createVersion = shouldCreateAutosaveVersion()
      saveModeRef.current = "auto"
      pendingVersionCreatedRef.current = createVersion
      updateDiagramMutation.mutate({
        id: hostedDiagram.id,
        title: diagram.name,
        description: diagram.description ?? null,
        specJson: diagram as unknown as Record<string, unknown>,
        createVersion,
        versionLabel: "auto",
      })
    }, AUTOSAVE_DEBOUNCE_MS)

    return () => window.clearTimeout(timeout)
  }, [diagram, hostedDiagram, isDirty, shouldCreateAutosaveVersion, updateDiagramMutation])

  useEffect(() => {
    if (!isDirty) return

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", warnBeforeUnload)
    return () => window.removeEventListener("beforeunload", warnBeforeUnload)
  }, [isDirty])

  const revertToVersion = useCallback(
    (versionId: string) => {
      if (!hostedDiagram) return
      revertDiagramMutation.mutate({ diagramId: hostedDiagram.id, versionId })
    },
    [hostedDiagram, revertDiagramMutation],
  )

  const canUndo = historyVersion >= 0 && undoStackRef.current.length > 0
  const canRedo = historyVersion >= 0 && redoStackRef.current.length > 0
  const versionRows = hostedVersions.data?.versions ?? []
  const activeSearchPosition = activeSearchMatchIndex === -1 ? 0 : activeSearchMatchIndex + 1
  const searchResultLabel = searchQuery.trim() ? `${activeSearchPosition}/${searchMatches.length}` : "0/0"
  const viewport = reactFlow.getViewport()
  const selectedFreeNode =
    selectedNodeIds.length === 1
      ? diagram.nodes.find((node) => node.id === selectedNodeIds[0] && !node.isGroup && !node.parentNode)
      : null

  if (showRegistryHome) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <div className="border-b border-border bg-sidebar px-6 py-5">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
            <div>
              <div className={SECTION_LABEL}>Diagrammer</div>
              <h1 className="text-2xl font-semibold tracking-tight">Hosted diagrams</h1>
            </div>
            <button
              type="button"
              className={TEXT_BUTTON}
              onClick={() => {
                window.history.pushState(null, "", "/diagrammer/new")
                loadDiagram(vidmaxSystemAtlas, "vidmax.json")
                setShowRegistryHome(false)
              }}
            >
              <Square className="h-4 w-4" />
              New diagram
            </button>
          </div>
        </div>

        <main className="mx-auto max-w-7xl px-6 py-6">
          {hostedList.isLoading && <div className="text-sm text-muted-foreground">Loading diagrams...</div>}
          {!hostedList.isLoading && hostedList.data?.diagrams.length === 0 && (
            <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
              No hosted diagrams yet.
            </div>
          )}
          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {hostedList.data?.diagrams.map((item) => {
              const previewSpec = isDiagramSpec(item.specJson) ? item.specJson : null
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    window.history.pushState(null, "", `/diagrammer/${item.slug}`)
                    setSelectedHostedId(item.id)
                    setShowRegistryHome(false)
                  }}
                  className="overflow-hidden rounded-lg border border-border bg-zinc-900 text-left shadow-sm transition-colors hover:border-zinc-600"
                >
                  <div className="h-36 border-b border-zinc-700 bg-white">
                    <DiagramPreviewCard spec={previewSpec} />
                  </div>
                  <div className="flex items-center gap-3 p-4">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-violet-600 text-white">
                      <Square className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-zinc-100">{item.title}</div>
                      <div className="truncate text-xs text-zinc-400">
                        {item.description || `Edited ${new Date(item.updatedAt).toLocaleDateString()}`}
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </main>
      </div>
    )
  }

  const loadBuiltInDiagram = (spec: DiagramSpec) => {
    window.location.hash = spec.id
    loadDiagram(spec, `${spec.id}.ts`)
  }
  return (
    <div
      className={`flex h-screen ${
        renderMode ? "bg-white text-slate-950" : "bg-background text-foreground"
      } ${renderReady ? "diagrammer-render-ready" : ""} ${exportReady ? "diagrammer-export-ready" : ""}`}
      data-diagrammer-render-ready={renderReady ? "true" : "false"}
      data-diagrammer-export-ready={exportReady ? "true" : "false"}
    >
      {/* Sidebar */}
      {!renderMode && (
        <aside className="w-56 bg-sidebar border-r border-sidebar-border flex flex-col gap-6 p-5 flex-shrink-0 overflow-y-auto">
          <div>
            {hostedDiagram && (
              <button type="button" className={`${TEXT_BUTTON} mb-3`} onClick={navigateToDiagramRegistry}>
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>
            )}
            <div className={SECTION_LABEL}>Diagrammer</div>
            {isEditingDiagramHeader ? (
              <div
                ref={diagramHeaderEditorRef}
                className="flex flex-col gap-2"
                onBlur={(event) => {
                  const nextTarget = event.relatedTarget
                  if (nextTarget instanceof Node && diagramHeaderEditorRef.current?.contains(nextTarget)) return
                  commitDiagramHeader()
                }}
              >
                <input
                  autoFocus
                  value={diagramHeaderDraft.name}
                  onChange={(event) => setDiagramHeaderDraft((previous) => ({ ...previous, name: event.target.value }))}
                  className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-base font-bold text-foreground outline-hidden focus:border-primary"
                  aria-label="Diagram title"
                  onKeyDown={(event) => {
                    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                      event.preventDefault()
                      commitDiagramHeader()
                    }
                    if (event.key === "Escape") {
                      event.preventDefault()
                      cancelDiagramHeaderEdit()
                    }
                  }}
                />
                <textarea
                  value={diagramHeaderDraft.description}
                  onChange={(event) =>
                    setDiagramHeaderDraft((previous) => ({ ...previous, description: event.target.value }))
                  }
                  className="min-h-20 w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 text-xs leading-relaxed text-foreground outline-hidden focus:border-primary"
                  aria-label="Diagram description"
                  onKeyDown={(event) => {
                    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                      event.preventDefault()
                      commitDiagramHeader()
                    }
                    if (event.key === "Escape") {
                      event.preventDefault()
                      cancelDiagramHeaderEdit()
                    }
                  }}
                />
              </div>
            ) : (
              <button
                type="button"
                className="block w-full rounded-md px-1 py-1 text-left hover:bg-sidebar-accent/50 focus:outline-hidden focus:ring-2 focus:ring-primary"
                onClick={startEditingDiagramHeader}
                aria-label="Edit diagram title"
              >
                <div className="text-base font-bold text-foreground">{diagram.name}</div>
                {diagram.description && (
                  <div className="mt-1 text-xs leading-relaxed text-muted-foreground">{diagram.description}</div>
                )}
              </button>
            )}
          </div>

          <FilePicker onLoad={loadDiagram} currentName={diagram.name} />

          <div>
            <div className={SECTION_LABEL}>Hosted</div>
            <div className="flex flex-col gap-1">
              {hostedList.isLoading && <div className="text-xs text-muted-foreground">Loading diagrams...</div>}
              {hostedList.data?.diagrams.slice(0, 8).map((item) => {
                const isActive = hostedDiagram?.id === item.id
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setSelectedHostedId(item.id)
                      window.history.replaceState(null, "", `#${item.slug}`)
                    }}
                    className={`rounded-md px-3 py-1.5 text-left text-xs transition-colors ${
                      isActive
                        ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold"
                        : "text-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    }`}
                  >
                    <div className="truncate">{item.title}</div>
                    <div className="truncate text-[10px] opacity-70">{item.project}</div>
                  </button>
                )
              })}
              {!hostedList.isLoading && hostedList.data?.diagrams.length === 0 && (
                <div className="text-xs text-muted-foreground">No hosted diagrams yet.</div>
              )}
            </div>
          </div>

          <div>
            <div className={SECTION_LABEL}>Library</div>
            <div className="flex flex-col gap-1">
              {DIAGRAM_LIST.map((item) => {
                const isActive = diagram.id === item.id
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => loadBuiltInDiagram(item)}
                    className={`rounded-md px-3 py-1.5 text-left text-sm transition-colors ${
                      isActive
                        ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold"
                        : "text-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    }`}
                  >
                    <span className="block truncate">{item.name}</span>
                    <span className="block truncate text-[10px] font-mono opacity-60">{item.id}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <div className={SECTION_LABEL}>Create</div>
            <div className="grid grid-cols-2 gap-1.5">
              <button type="button" className={TOOL_BUTTON} onClick={addNode} title="Add node" aria-label="Add node">
                <Square className="h-4 w-4" />
                Node
              </button>
              <button
                type="button"
                className={TOOL_BUTTON}
                onClick={() => addSection()}
                title="Add section"
                aria-label="Add section"
              >
                <SquareDashedMousePointer className="h-4 w-4" />
                Section
              </button>
              <button
                type="button"
                className={TOOL_BUTTON}
                onClick={startLineTool}
                title="Draw line"
                aria-label="Draw line"
              >
                <GitBranch className="h-4 w-4" />
                Line
              </button>
            </div>
            {selectedNodeIds.length > 1 && (
              <div className="mt-2 text-xs text-muted-foreground">{selectedNodeIds.length} nodes selected</div>
            )}
            {copiedSelection && (
              <div className="mt-2 text-xs text-muted-foreground">
                Copied {copiedSelection.nodes.length}; paste with Cmd+V or duplicate with Cmd+D.
              </div>
            )}
            {pendingLineSourceId && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <MousePointer2 className="h-3 w-3" />
                Click a target node
              </div>
            )}
          </div>

          <div>
            <div className={SECTION_LABEL}>Selection</div>
            <div className="rounded-md border border-border bg-background px-3 py-2">
              {selectedFreeNode ? (
                <div className="flex flex-col gap-2">
                  <div className="truncate text-xs font-semibold text-foreground">{selectedFreeNode.label}</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      type="button"
                      className={TOOL_BUTTON}
                      onClick={() => moveSelectedNodeZOrder(1)}
                      title="Bring selected node forward"
                      aria-label="Bring selected node forward"
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                      Front
                    </button>
                    <button
                      type="button"
                      className={TOOL_BUTTON}
                      onClick={() => moveSelectedNodeZOrder(-1)}
                      title="Send selected node backward"
                      aria-label="Send selected node backward"
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                      Back
                    </button>
                  </div>
                </div>
              ) : selectedNode?.isGroup ? (
                <button type="button" className={TEXT_BUTTON} onClick={() => fixSectionBounds(selectedNode.id)}>
                  Fix section bounds
                </button>
              ) : selectedEdge ? (
                <div className="text-xs leading-relaxed text-muted-foreground">
                  <div className="font-semibold text-foreground">Edge selected</div>
                  Drag an endpoint handle to reconnect.
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">Select a node, section, or edge.</div>
              )}
            </div>
          </div>

          <div>
            <div className={SECTION_LABEL}>Hosted Actions</div>
            <div className="flex flex-col gap-1.5">
              <button
                type="button"
                className={TEXT_BUTTON}
                onClick={saveDiagram}
                disabled={createDiagram.isLoading || updateDiagramMutation.isLoading}
              >
                <Save className="h-3.5 w-3.5" />
                Save
              </button>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {createDiagram.isLoading || updateDiagramMutation.isLoading ? (
                  "Saving..."
                ) : isDirty ? (
                  "Unsaved changes"
                ) : hostedDiagram ? (
                  <>
                    <Check className="h-3 w-3" />
                    {lastSavedAt ? `Saved ${lastSavedAt.toLocaleTimeString()}` : "Saved"}
                  </>
                ) : (
                  "Save to host to persist"
                )}
              </div>
              <button
                type="button"
                className={TEXT_BUTTON}
                onClick={publishCurrentDiagram}
                disabled={!hostedDiagram || publishDiagram.isLoading}
              >
                <Share2 className="h-3.5 w-3.5" />
                Publish
              </button>
              <button type="button" className={TEXT_BUTTON} onClick={copyPublishUrl} disabled={!hostedDiagram}>
                Copy URL
              </button>
              <button type="button" className={TEXT_BUTTON} onClick={openRawEditor}>
                <Code2 className="h-3.5 w-3.5" />
                Raw AIXML
              </button>
            </div>
          </div>

          <div>
            <div className={SECTION_LABEL}>Version History</div>
            {!hostedDiagram ? (
              <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                Save to host first.
              </div>
            ) : hostedVersions.isLoading ? (
              <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                Loading history...
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-background">
                <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
                    <History className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-semibold text-foreground">Current</div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {lastSavedAt ? formatVersionTime(lastSavedAt) : "Not saved yet"}
                    </div>
                  </div>
                </div>
                {versionRows.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-muted-foreground">No snapshots yet.</div>
                ) : (
                  versionRows.map((version) => (
                    <div
                      key={version.id}
                      className="flex items-center gap-2 border-b border-border px-3 py-2 last:border-b-0"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-medium text-foreground">{version.versionLabel}</div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {formatVersionTime(version.createdAt)}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-[11px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
                        onClick={() => revertToVersion(version.id)}
                        disabled={revertDiagramMutation.isLoading}
                        title={`Revert to ${version.versionLabel}`}
                        aria-label={`Revert to ${version.versionLabel}`}
                      >
                        <RotateCcw className="h-3 w-3" />
                        Revert
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div>
            <div className={SECTION_LABEL}>Views</div>
            <div className="flex flex-col gap-1">
              {diagram.views.map((view) => {
                const isActive = activeView === view.id
                return (
                  <button
                    key={view.id}
                    onClick={() => switchView(view.id)}
                    className={`rounded-md px-3 py-1.5 text-left text-sm transition-colors ${
                      isActive
                        ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold"
                        : "text-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    }`}
                  >
                    {view.name}
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <div className={SECTION_LABEL}>Layers</div>
            <div className="flex flex-col gap-2">
              {diagram.layers.map((layer) => {
                const on = activeLayers.has(layer.id)
                return (
                  <button
                    key={layer.id}
                    type="button"
                    onClick={() => toggleLayer(layer.id)}
                    className={`flex items-center gap-2 text-left text-sm select-none transition-colors ${
                      on ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    <span
                      className="w-3 h-3 rounded-sm border-2 flex-shrink-0 transition-all"
                      style={{
                        background: on ? layer.color : "transparent",
                        borderColor: on ? layer.color : undefined,
                      }}
                    />
                    {layer.name}
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <div className={SECTION_LABEL}>Display</div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={prefs.showEdgeLabels}
                onChange={(event) => setShowEdgeLabels(event.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
              />
              Edge labels
            </label>
            <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Grid3X3 className="h-3.5 w-3.5" />
              Snap {SNAP_GRID_SIZE}px
            </div>
          </div>

          <div>
            <div className={SECTION_LABEL}>Quality</div>
            <div className="rounded-md border border-border bg-background">
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <AlertTriangle className={`h-4 w-4 ${qualityResult.ok ? "text-emerald-400" : "text-amber-400"}`} />
                <div className="min-w-0 flex-1 text-xs font-semibold text-foreground">
                  {qualityResult.ok
                    ? "No blocking issues"
                    : `${qualityResult.counts.errors} errors · ${qualityResult.counts.warnings} warnings`}
                </div>
              </div>
              {qualityResult.issues.length === 0 ? (
                <div className="px-3 py-3 text-xs text-muted-foreground">Diagram passes current quality gates.</div>
              ) : (
                <div className="max-h-72 overflow-y-auto">
                  {qualityResult.issues.slice(0, 8).map((item) => {
                    const sectionId = item.nodeIds?.find((nodeId) =>
                      diagram.nodes.some((node) => node.id === nodeId && node.isGroup),
                    )
                    const canFixSection = Boolean(
                      sectionId && (item.code === "section-too-small" || item.code === "child-outside-section"),
                    )
                    return (
                      <div key={item.id} className="border-b border-border px-3 py-2 last:border-b-0">
                        <button
                          type="button"
                          className="block w-full text-left"
                          onClick={() => {
                            const nodeId = item.nodeIds?.[0]
                            if (nodeId) selectAndFocusNode(nodeId)
                          }}
                        >
                          <div
                            className={`mb-1 inline-flex rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${getQualityTone(item.severity)}`}
                          >
                            {item.severity}
                          </div>
                          <div className="text-xs leading-snug text-foreground">{item.message}</div>
                          {item.action && (
                            <div className="mt-1 text-[11px] leading-snug text-muted-foreground">{item.action}</div>
                          )}
                        </button>
                        {canFixSection && sectionId && (
                          <button
                            type="button"
                            className={`${TEXT_BUTTON} mt-2 h-7`}
                            onClick={() => fixSectionBounds(sectionId)}
                          >
                            Fix section bounds
                          </button>
                        )}
                      </div>
                    )
                  })}
                  {qualityResult.issues.length > 8 && (
                    <div className="px-3 py-2 text-[11px] text-muted-foreground">
                      {qualityResult.issues.length - 8} more issues hidden.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className={SECTION_LABEL}>Export</div>
            <ExportButton fileName={fileName} />
          </div>

          <div className="mt-auto pt-4 border-t border-sidebar-border">
            <div className="text-xs text-muted-foreground">
              {diagram.nodes.length} nodes · {diagram.edges.length} edges · {diagram.layers.length} layers
            </div>
          </div>
        </aside>
      )}

      {/* Canvas */}
      <div className="flex-1 relative" ref={canvasRef}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onDiagramNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onNodeDoubleClick={onNodeDoubleClick}
          onEdgeClick={onEdgeClick}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          onConnect={onConnect}
          onEdgeUpdate={onEdgeUpdate}
          onSelectionChange={onSelectionChange}
          onPaneClick={handlePaneClick}
          nodeTypes={nodeTypes}
          selectionOnDrag
          selectionMode={SelectionMode.Partial}
          panOnDrag={[1, 2]}
          proOptions={{ hideAttribution: true }}
          fitView
          fitViewOptions={{ padding: prefs.fitPadding, maxZoom: prefs.maxZoom }}
          minZoom={0.2}
          maxZoom={3}
        >
          <Background gap={28} size={1} color={renderMode ? "#d1d5db" : undefined} />
          {prefs.showLayerBands && <LayerBands direction={currentView.layout} />}
          {!renderMode && <Controls />}
        </ReactFlow>
        {!renderMode && alignmentGuide?.x !== undefined && (
          <div
            className="pointer-events-none absolute inset-y-0 z-10 w-px bg-amber-400/80"
            style={{ left: alignmentGuide.x * viewport.zoom + viewport.x }}
          />
        )}
        {!renderMode && alignmentGuide?.y !== undefined && (
          <div
            className="pointer-events-none absolute inset-x-0 z-10 h-px bg-amber-400/80"
            style={{ top: alignmentGuide.y * viewport.zoom + viewport.y }}
          />
        )}
        {!renderMode && isSearchOpen && (
          <div className="absolute right-5 top-5 z-20 flex w-[360px] max-w-[calc(100vw-2rem)] items-center gap-2 rounded-lg border border-border bg-background/95 p-2 shadow-lg backdrop-blur">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value)
                setActiveSearchMatchIndex(-1)
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  focusNextSearchMatch(event.shiftKey ? -1 : 1)
                }
                if (event.key === "Escape") {
                  event.preventDefault()
                  setIsSearchOpen(false)
                }
              }}
              className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-sm text-foreground outline-hidden focus:border-primary"
              placeholder="Search nodes"
              aria-label="Search diagram nodes"
            />
            <div className="min-w-14 text-right text-xs text-muted-foreground">{searchResultLabel}</div>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
              onClick={() => {
                setIsSearchOpen(false)
                setSearchQuery("")
                setActiveSearchMatchIndex(-1)
              }}
              aria-label="Close search"
              title="Close search"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {!renderMode && showRawEditor && (
          <div className="absolute inset-y-0 right-0 z-20 flex w-[min(720px,calc(100vw-14rem))] flex-col border-l border-border bg-background shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <div>
                <div className={SECTION_LABEL}>Raw AIXML</div>
                <div className="text-sm font-semibold text-foreground">{diagram.name}</div>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" className={TEXT_BUTTON} onClick={() => setShowRawEditor(false)}>
                  Cancel
                </button>
                <button type="button" className={TEXT_BUTTON} onClick={applyRawDiagram}>
                  Apply
                </button>
              </div>
            </div>
            {rawDiagramError && (
              <div className="border-b border-destructive/30 bg-destructive/10 px-4 py-3 text-xs leading-relaxed text-destructive whitespace-pre-wrap">
                {rawDiagramError}
              </div>
            )}
            <textarea
              value={rawDiagramDraft}
              onChange={(event) => {
                setRawDiagramDraft(event.target.value)
                if (rawDiagramError) setRawDiagramError(null)
              }}
              spellCheck={false}
              className="min-h-0 flex-1 resize-none bg-zinc-950 px-4 py-3 font-mono text-xs leading-relaxed text-zinc-100 outline-hidden"
              aria-label="Raw AIXML JSON"
            />
          </div>
        )}
        {!renderMode && (
          <div className="pointer-events-none absolute inset-x-0 bottom-5 z-10 flex justify-center">
            <div className="pointer-events-auto flex items-center gap-1 rounded-lg border border-border bg-background/95 p-1 shadow-lg backdrop-blur">
              {[
                { id: "node" as const, label: "Node", key: "N", icon: Square },
                { id: "section" as const, label: "Section", key: "S", icon: SquareDashedMousePointer },
              ].map((tool) => {
                const Icon = tool.icon
                const isActive = activeTool === tool.id
                return (
                  <button
                    key={tool.id}
                    type="button"
                    className={`inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`}
                    onClick={() => setActiveTool(tool.id)}
                    title={`Place ${tool.label} (${tool.key})`}
                    aria-label={`Place ${tool.label} (${tool.key})`}
                  >
                    <Icon className="h-4 w-4" />
                    {tool.label}
                    <KeyBadge value={tool.key} />
                  </button>
                )
              })}
              <div className="mx-1 h-5 w-px bg-border" />
              <button
                type="button"
                className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
                onClick={startLineTool}
                title="Draw line (L)"
                aria-label="Draw line (L)"
              >
                <GitBranch className="h-4 w-4" />
                Line
                <KeyBadge value="L" />
              </button>
              <button
                type="button"
                className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
                onClick={() => void centerActiveView()}
                title="Center active view (C)"
                aria-label="Center active view (C)"
              >
                <Crosshair className="h-4 w-4" />
                Center
                <KeyBadge value="C" />
              </button>
              <div className="mx-1 h-5 w-px bg-border" />
              <button
                type="button"
                className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
                onClick={undoDiagramEdit}
                disabled={!canUndo}
                title="Undo (U)"
                aria-label="Undo (U)"
              >
                <RotateCcw className="h-4 w-4" />
                Undo
                <KeyBadge value="U" />
              </button>
              <button
                type="button"
                className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
                onClick={redoDiagramEdit}
                disabled={!canRedo}
                title="Redo (R)"
                aria-label="Redo (R)"
              >
                <RotateCw className="h-4 w-4" />
                Redo
                <KeyBadge value="R" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

interface DiagrammerScreenProps {
  renderMode?: boolean
}

export default function DiagrammerScreen({ renderMode = false }: DiagrammerScreenProps) {
  return (
    <ReactFlowProvider>
      <DiagramApp renderMode={renderMode} />
    </ReactFlowProvider>
  )
}
